import { useTranslation } from 'react-i18next';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';
import { useTheme } from '../../../hooks/useTheme';
import Card3D from '../../../components/base/Card3D';
import ParticleBackground from '../../../components/base/ParticleBackground';
import AnimatedCounter from '../../../components/base/AnimatedCounter';
import GlowCard from '../../../components/base/GlowCard';

export default function CoreCapabilities() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: leftCardRef, isVisible: leftCardVisible } = useScrollAnimation();
  const { ref: rightCardsRef, isVisible: rightCardsVisible, getStaggerDelay } = useStaggerAnimation(5);

  return (
    <section
      id="core-capabilities"
      className={`relative py-16 md:py-24 lg:py-32 overflow-hidden transition-colors duration-500 ${
        isDark
          ? 'bg-gradient-to-b from-[#0d1117] to-[#161b22]'
          : 'bg-gradient-to-b from-white to-[#f8fafb]'
      }`}
    >
      {/* 背景装饰 */}
      <div
        className={`absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/10' : 'from-[#E84A1F]/5'
        } via-transparent to-transparent blur-3xl`}
      ></div>
      <div
        className={`absolute bottom-0 left-0 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-radial ${
          isDark ? 'from-[#F06030]/8' : 'from-[#F06030]/3'
        } via-transparent to-transparent blur-3xl`}
      ></div>

      {/* 中心大光晕 */}
      <div
        className={`absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-gradient-radial ${
          isDark ? 'from-[#E84A1F]/5' : 'from-[#E84A1F]/3'
        } via-transparent to-transparent blur-[80px]`}
      ></div>

      {/* 装饰性圆环 */}
      <div
        className={`absolute top-[10%] left-[5%] w-[150px] md:w-[300px] h-[150px] md:h-[300px] border ${
          isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
        } rounded-full hidden md:block`}
      ></div>
      <div
        className={`absolute bottom-[15%] right-[8%] w-[200px] md:w-[400px] h-[200px] md:h-[400px] border border-dashed ${
          isDark ? 'border-[#F06030]/10' : 'border-[#F06030]/5'
        } rounded-full hidden md:block`}
      ></div>

      {/* 网格背景 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'}`}
        style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'} 1px, transparent 1px), linear-gradient(90deg, ${
            isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,17,23,0.5)'
          } 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      ></div>

      {/* 点阵装饰 */}
      <div
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
        style={{
          backgroundImage: 'radial-gradient(circle, #E84A1F 1.5px, transparent 1.5px)',
          backgroundSize: '50px 50px',
        }}
      ></div>

      {/* 粒子背景效果 */}
      <ParticleBackground 
        particleCount={60} 
        speed={0.3} 
        connectDistance={100}
      />

      {/* 渐变条纹（已修正不合法的 Tailwind 类） */}
      <div
        className={`absolute top-0 left-0 w-full h-[300px] md:h-[500px] bg-gradient-to-b ${
          isDark ? 'from-[#E84A1F]/[0.03]' : 'from-[#E84A1F]/[0.02]'
        } to-transparent pointer-events-none`}
      ></div>
      <div
        className={`absolute bottom-0 left-0 w-full h-[200px] md:h-[400px] bg-gradient-to-t ${
          isDark ? 'from-[#161b22]' : 'from-[#f8fafb]'
        } to-transparent pointer-events-none`}
      ></div>

      {/* 浮动装饰元素 */}
      <div
        className={`absolute top-[20%] right-[10%] w-8 h-8 border-2 ${
          isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
        } rounded-lg rotate-12 hidden lg:block`}
      ></div>
      <div
        className={`absolute top-[60%] left-[5%] w-6 h-6 ${
          isDark ? 'bg-[#F06030]/10' : 'bg-[#F06030]/5'
        } rounded-full hidden lg:block`}
      ></div>
      <div
        className={`absolute bottom-[25%] right-[15%] w-4 h-4 ${
          isDark ? 'bg-[#E84A1F]/10' : 'bg-[#E84A1F]/5'
        } rounded-full hidden lg:block`}
      ></div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 mb-10 md:mb-16 relative transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* 装饰性大号数字 */}
          <div
            className={`absolute -top-8 left-0 text-[100px] md:text-[180px] font-bold ${
              isDark ? 'text-white/[0.02]' : 'text-[#0d1117]/[0.02]'
            } leading-none select-none pointer-events-none hidden md:block`}
          >
            01
          </div>

          {/* 左侧编号栏 */}
          <div className="flex md:flex-col items-center gap-3 pt-0 md:pt-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20">
              <span className="text-white font-bold text-base md:text-lg">01</span>
            </div>
            <div
              className={`hidden md:block w-px bg-gradient-to-b from-[#E84A1F] to-transparent transition-all duration-1000 delay-300 ${
                headerVisible ? 'h-16' : 'h-0'
              }`}
            ></div>
          </div>

          {/* 标题内容 */}
          <div className="flex-1 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-0">
            <div className="relative">
              <div className="inline-flex items-center gap-3 mb-3 md:mb-4">
                <div className="w-2 h-2 bg-[#E84A1F] rounded-full animate-pulse"></div>
                <span className="text-xs text-[#E84A1F] tracking-[0.2em] md:tracking-[0.3em] uppercase">
                  Core Features
                </span>
              </div>
              <h2
                className={`text-3xl md:text-4xl lg:text-5xl font-bold transition-colors duration-500 ${
                  isDark ? 'text-white' : 'text-[#0d1117]'
                }`}
              >
                {t('core.title')}
              </h2>
            </div>

            <div
              className={`max-w-full md:max-w-[380px] text-sm leading-relaxed pb-0 md:pb-2 border-l-0 md:border-l border-[#E84A1F]/30 pl-0 md:pl-6 transition-all duration-1000 delay-500 ${
                isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
              } ${headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 md:translate-x-10'}`}
            >
              {t('core.subtitle')}
            </div>
          </div>
        </div>

        {/* 主内容区 - Bento Grid 布局 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          {/* 左侧大卡片 - 高性能执行 */}
          <div
            ref={leftCardRef}
            className={`lg:col-span-5 lg:row-span-2 relative group transition-all duration-1000 ${
              leftCardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 lg:-translate-x-10'
            }`}
          >
            <Card3D className="h-full" intensity={8} scale={1.02}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-2xl md:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-2xl md:rounded-3xl p-5 md:p-8 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden flex flex-col ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-lg shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-lg shadow-[#0d1117]/5'
                }`}
              >
                {/* 装饰性圆环 */}
                <div
                  className={`absolute -top-20 -right-20 w-60 h-60 border ${
                    isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                  } rounded-full hidden md:block`}
                ></div>
                <div
                  className={`absolute -top-10 -right-10 w-40 h-40 border ${
                    isDark ? 'border-[#E84A1F]/10' : 'border-[#E84A1F]/5'
                  } rounded-full hidden md:block`}
                ></div>

                {/* 顶部标签 */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="inline-flex items-center gap-2 px-2.5 md:px-3 py-1 md:py-1.5 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-[#E84A1F] rounded-full animate-pulse"></div>
                    <span className="text-[10px] text-[#E84A1F] uppercase tracking-wider">
                      High Performance
                    </span>
                  </div>
                  <div className={`text-xs ${isDark ? 'text-white/20' : 'text-[#0d1117]/20'}`}>01</div>
                </div>

                {/* 图标和标题区域 */}
                <div className="flex items-start gap-4 md:gap-5 mb-4 md:mb-6">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 flex items-center justify-center hidden md:flex">
                      <div
                        className={`w-20 h-20 border ${
                          isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                        } rounded-full animate-[spin_20s_linear_infinite]`}
                      ></div>
                      <div
                        className={`absolute w-16 h-16 border ${
                          isDark ? 'border-[#E84A1F]/30' : 'border-[#E84A1F]/20'
                        } rounded-full animate-[spin_15s_linear_infinite_reverse]`}
                      ></div>
                    </div>
                    <div className="relative w-12 md:w-14 h-12 md:h-14 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-xl md:rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(232,74,31,0.4)] group-hover:shadow-[0_0_60px_rgba(232,74,31,0.5)] transition-all">
                      <i className="ri-flashlight-line text-xl md:text-2xl text-white"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-xl md:text-2xl font-bold mb-1 md:mb-2 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('core.performance.title')}
                    </h3>
                    <p
                      className={`text-xs md:text-sm leading-relaxed ${
                        isDark ? 'text-gray-400' : 'text-[#0d1117]/50'
                      }`}
                    >
                      {t('core.performance.desc')}
                    </p>
                  </div>
                </div>

                {/* 功能特性 */}
                <div className="space-y-2 md:space-y-3 mb-4 md:mb-6 flex-1">
                  {[
                    { icon: 'ri-speed-up-line', text: t('core.performance.feature1') },
                    { icon: 'ri-database-2-line', text: t('core.performance.feature2') },
                    { icon: 'ri-shield-flash-line', text: t('core.performance.feature3') },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-2.5 md:p-3 border rounded-lg md:rounded-xl group/item hover:border-[#E84A1F]/20 transition-all ${
                        isDark ? 'bg-[#0d1117] border-white/5' : 'bg-[#f8fafb] border-[#0d1117]/5'
                      }`}
                    >
                      <div className="w-7 md:w-8 h-7 md:h-8 bg-[#E84A1F]/10 rounded-lg flex items-center justify-center">
                        <i className={`${item.icon} text-[#E84A1F] text-xs md:text-sm`}></i>
                      </div>
                      <span
                        className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-[#0d1117]/60'}`}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* 性能指标 - 使用 AnimatedCounter */}
                <div className="grid grid-cols-3 gap-2 md:gap-3 mt-auto">
                  <div
                    className={`border rounded-lg md:rounded-xl p-3 md:p-4 text-center group/stat hover:border-[#E84A1F]/30 transition-all cursor-default ${
                      isDark ? 'bg-[#0d1117] border-white/5' : 'bg-[#f8fafb] border-[#0d1117]/5'
                    }`}
                  >
                    <div className="text-lg md:text-2xl font-bold bg-gradient-to-r from-[#E84A1F] to-[#F06030] bg-clip-text text-transparent group-hover/stat:scale-110 transition-transform">
                      <AnimatedCounter end={10000} suffix="+" duration={2000} />
                    </div>
                    <div className={`text-[10px] md:text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>TPS</div>
                  </div>
                  <div
                    className={`border rounded-lg md:rounded-xl p-3 md:p-4 text-center group/stat hover:border-[#E84A1F]/30 transition-all cursor-default ${
                      isDark ? 'bg-[#0d1117] border-white/5' : 'bg-[#f8fafb] border-[#0d1117]/5'
                    }`}
                  >
                    <div className="text-lg md:text-2xl font-bold bg-gradient-to-r from-[#F06030] to-[#E84A1F] bg-clip-text text-transparent group-hover/stat:scale-110 transition-transform">
                      &lt;2s
                    </div>
                    <div className={`text-[10px] md:text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Finality</div>
                  </div>
                  <div
                    className={`border rounded-lg md:rounded-xl p-3 md:p-4 text-center group/stat hover:border-[#E84A1F]/30 transition-all cursor-default ${
                      isDark ? 'bg-[#0d1117] border-white/5' : 'bg-[#f8fafb] border-[#0d1117]/5'
                    }`}
                  >
                    <div className="text-lg md:text-2xl font-bold bg-gradient-to-r from-[#E84A1F] to-[#F06030] bg-clip-text text-transparent group-hover/stat:scale-110 transition-transform">
                      <AnimatedCounter end={99.9} suffix="%" duration={2000} decimals={1} />
                    </div>
                    <div className={`text-[10px] md:text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Uptime</div>
                  </div>
                </div>
              </div>
            </Card3D>
          </div>

          {/* 中间卡片 - 分层模块化架构 - 使用 GlowCard */}
          <div
            ref={rightCardsRef}
            className={`lg:col-span-4 relative group transition-all duration-700 ${
              rightCardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={getStaggerDelay(0)}
          >
            <Card3D className="h-full" intensity={10} scale={1.03}>
              <GlowCard glowColor="#E84A1F" glowIntensity={0.2} className="h-full rounded-2xl md:rounded-3xl">
                <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-2xl md:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div
                  className={`relative border rounded-2xl md:rounded-3xl p-5 md:p-6 h-full hover:border-[#E84A1F]/30 transition-all duration-500 ${
                    isDark
                      ? 'bg-[#161b22] border-white/10 shadow-lg shadow-black/20'
                      : 'bg-white border-[#0d1117]/10 shadow-lg shadow-[#0d1117]/5'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4 md:mb-5">
                    <div className="w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#F06030] to-[#E84A1F] rounded-lg md:rounded-xl flex items-center justify-center">
                      <i className="ri-stack-line text-lg md:text-xl text-white"></i>
                    </div>
                    <h3
                      className={`text-base md:text-lg font-semibold transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('core.modular.title')}
                    </h3>
                  </div>

                  {/* 架构层级 */}
                  <div className="space-y-2 relative">
                    <div className="absolute left-5 top-8 bottom-8 w-px bg-gradient-to-b from-[#E84A1F]/50 via-[#F06030]/50 to-[#C83A15]/50"></div>

                    <div className="relative flex items-center gap-3 bg-gradient-to-r from-[#E84A1F]/10 to-transparent border border-[#E84A1F]/20 rounded-lg md:rounded-xl p-2.5 md:p-3 pl-4">
                      <div className="w-2 h-2 bg-[#E84A1F] rounded-full relative z-10"></div>
                      <div>
                        <div className="text-xs md:text-sm font-medium text-[#E84A1F]">{t('core.modular.consensus')}</div>
                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('core.modular.consensus.desc')}</div>
                      </div>
                    </div>

                    <div className="relative flex items-center gap-3 bg-gradient-to-r from-[#F06030]/10 to-transparent border border-[#F06030]/20 rounded-lg md:rounded-xl p-2.5 md:p-3 pl-4">
                      <div className="w-2 h-2 bg-[#F06030] rounded-full relative z-10"></div>
                      <div>
                        <div className="text-xs md:text-sm font-medium text-[#F06030]">{t('core.modular.execution')}</div>
                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('core.modular.execution.desc')}</div>
                      </div>
                    </div>

                    <div className="relative flex items-center gap-3 bg-gradient-to-r from-[#C83A15]/10 to-transparent border border-[#C83A15]/20 rounded-lg md:rounded-xl p-2.5 md:p-3 pl-4">
                      <div className="w-2 h-2 bg-[#F58050] rounded-full relative z-10"></div>
                      <div>
                        <div className="text-xs md:text-sm font-medium text-[#F58050]">{t('core.modular.data')}</div>
                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/50'}`}>{t('core.modular.data.desc')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </GlowCard>
            </Card3D>
          </div>

          {/* 右上卡片 - 跨链互操作 */}
          <div
            className={`lg:col-span-3 relative group transition-all duration-700 ${
              rightCardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={getStaggerDelay(1)}
          >
            <Card3D className="h-full" intensity={12} scale={1.04}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/10 to-transparent rounded-2xl md:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-2xl md:rounded-3xl p-5 md:p-6 h-full hover:border-[#E84A1F]/30 transition-all duration-500 overflow-hidden ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-lg shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-lg shadow-[#0d1117]/5'
                }`}
              >
                <h3
                  className={`text-base md:text-lg font-semibold mb-3 md:mb-4 text-center transition-colors duration-500 ${
                    isDark ? 'text-white' : 'text-[#0d1117]'
                  }`}
                >
                  {t('core.crosschain.title')}
                </h3>

                {/* 网络节点动画 */}
                <div className="relative h-24 md:h-28">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 md:w-10 h-9 md:h-10 bg-gradient-to-br from-[#E84A1F] to-[#F06030] rounded-full flex items-center justify-center shadow-lg shadow-[#E84A1F]/30 z-10">
                    <i className="ri-links-line text-base md:text-lg text-white"></i>
                  </div>

                  <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 md:w-24 h-20 md:h-24 border ${
                      isDark ? 'border-[#E84A1F]/30' : 'border-[#E84A1F]/20'
                    } rounded-full animate-[spin_10s_linear_infinite]`}
                  >
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 md:w-3 h-2.5 md:h-3 bg-[#E84A1F]/60 rounded-full"></div>
                    <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2.5 md:w-3 h-2.5 md:h-3 bg-[#F06030]/60 rounded-full"></div>
                  </div>

                  <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 md:w-20 h-16 md:h-20 border border-dashed ${
                      isDark ? 'border-[#E84A1F]/20' : 'border-[#E84A1F]/10'
                    } rounded-full animate-[spin_8s_linear_infinite_reverse]`}
                  >
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-[#E84A1F]/40 rounded-full"></div>
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 md:w-2 h-1.5 md:h-2 bg-[#F06030]/40 rounded-full"></div>
                  </div>
                </div>

                <p
                  className={`text-xs leading-relaxed text-center mt-2 md:mt-3 ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}
                >
                  {t('core.crosschain.desc')}
                </p>
              </div>
            </Card3D>
          </div>

          {/* 中下卡片 - RWA 原生设计 */}
          <div
            className={`lg:col-span-4 relative group transition-all duration-700 ${
              rightCardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={getStaggerDelay(2)}
          >
            <Card3D className="h-full" intensity={10} scale={1.03}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#C83A15]/10 to-transparent rounded-2xl md:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-2xl md:rounded-3xl p-5 md:p-6 hover:border-[#E84A1F]/30 transition-all duration-500 ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-lg shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-lg shadow-[#0d1117]/5'
                }`}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#E84A1F] to-[#C83A15] rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#E84A1F]/20">
                    <i className="ri-shield-check-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4
                      className={`text-sm md:text-base font-semibold mb-1 md:mb-2 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('core.rwa.title')}
                    </h4>
                    <p
                      className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}
                    >
                      {t('core.rwa.desc')}
                    </p>
                  </div>
                </div>

                {/* 特性标签 */}
                <div className="flex flex-wrap gap-1.5 md:gap-2 mt-3 md:mt-4 pl-0 md:pl-16">
                  {[t('core.rwa.tag1'), t('core.rwa.tag2'), t('core.rwa.tag3')].map((tag, index) => (
                    <span
                      key={index}
                      className="px-2.5 md:px-3 py-1 bg-[#E84A1F]/10 border border-[#E84A1F]/20 rounded-full text-[10px] text-[#E84A1F]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Card3D>
          </div>

          {/* 右下卡片 - 去中心化安全 */}
          <div
            className={`lg:col-span-3 relative group transition-all duration-700 ${
              rightCardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={getStaggerDelay(3)}
          >
            <Card3D className="h-full" intensity={12} scale={1.04}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#F06030]/10 to-transparent rounded-2xl md:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div
                className={`relative border rounded-2xl md:rounded-3xl p-5 md:p-6 hover:border-[#E84A1F]/30 transition-all duration-500 ${
                  isDark
                    ? 'bg-[#161b22] border-white/10 shadow-lg shadow-black/20'
                    : 'bg-white border-[#0d1117]/10 shadow-lg shadow-[#0d1117]/5'
                }`}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-gradient-to-br from-[#F06030] to-[#C83A15] rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#F06030]/20">
                    <i className="ri-lock-2-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4
                      className={`text-sm md:text-base font-semibold mb-1 md:mb-2 transition-colors duration-500 ${
                        isDark ? 'text-white' : 'text-[#0d1117]'
                      }`}
                    >
                      {t('core.security.title')}
                    </h4>
                    <p
                      className={`text-xs md:text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#0d1117]/50'}`}
                    >
                      {t('core.security.desc')}
                    </p>
                  </div>
                </div>

                {/* 安全指标 */}
                <div
                  className={`mt-3 md:mt-4 pt-3 md:pt-4 border-t flex items-center justify-between ${
                    isDark ? 'border-white/10' : 'border-[#0d1117]/10'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-base md:text-lg font-bold text-[#E84A1F]">99.9%</div>
                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Uptime</div>
                  </div>
                  <div className={`w-px h-6 md:h-8 ${isDark ? 'bg-white/10' : 'bg-[#0d1117]/10'}`}></div>
                  <div className="text-center">
                    <div className="text-base md:text-lg font-bold text-[#F06030]">24/7</div>
                    <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-[#0d1117]/40'}`}>Monitor</div>
                  </div>
                </div>
              </div>
            </Card3D>
          </div>
        </div>
      </div>
    </section>
  );
}
